import { useMemo } from "react";
import CustomTitle from "../../components/CustomTitle";
import Footer from "../../components/layouts/Footer";
import MailingList from "../../components/MailingList";
import LoginOrRegisterBanner from "../../components/LoginOrRegisterBanner";
import SwipperMultiple from "../../components/SwipperMultiple";
import { ArtworkCardProps } from "../../types";
import { usePersistStore, useStore } from "../../store";
import { AD_SORTING, AD_TYPES } from "../../utils/enums";
import ArtworkCardSkeletonRow from "../../components/ArtworkCardSkeletonRow";
import CategoryChips from "../../components/CategoryChips";
import { useAdSearch } from "../../hooks/queries";

// Map a raw ad into the ArtworkCard props shape shared by the result sections.
const toCard = (tt: any, color: boolean): ArtworkCardProps => ({
  _id: tt?._id,
  color,
  imageUrl: tt?.images?.[0],
  category: tt?.categoryId?.name,
  subCategory: tt?.subCategoryId?.name,
  views: tt?.views || 0,
  title: tt?.title,
  ownerImage: tt?.userId?.fileUrl,
  ownerName: tt?.userId?.name,
  ownerId: tt?.userId?._id,
  biddingStarts: tt?.biddingStarts,
  biddingEnds: tt?.biddingEnds,
  price: tt?.price,
  showBidNowButton: true,
  status: tt?.status,
  distance: tt?.distance,
});

function SearchResults() {
  const { keyword, priceRange, radius, mediumIds } = useStore((s: any) => s);
  const { token, user, currentLocation } = usePersistStore((s: any) => s);

  const baseBody = {
    page: 0,
    limit: 5,
    sorting: AD_SORTING.PRICE_LOW_TO_HIGH,
    keyword,
    range: priceRange,
    radius,
    mediumIds,
    location: currentLocation,
  };
  const q1 = useAdSearch({ ...baseBody, type: AD_TYPES.OPEN_FOR_BIDS });
  const q2 = useAdSearch({ ...baseBody, type: "popular" });
  const q3 = useAdSearch({ ...baseBody, type: AD_TYPES.UPCOMING });

  const list1 = useMemo(
    () => (q1.data?.ads || []).map((tt: any) => toCard(tt, true)),
    [q1.data]
  );
  const list2 = useMemo(
    () => (q2.data?.ads || []).map((tt: any) => toCard(tt, false)),
    [q2.data]
  );
  const list3 = useMemo(
    () => (q3.data?.ads || []).map((tt: any) => toCard(tt, false)),
    [q3.data]
  );
  const isReady1 = !q1.isLoading;
  const isReady2 = !q2.isLoading;
  const isReady3 = !q3.isLoading;

  return (
    <>
      <div className="container px-md-0 px-3">
        <div className="row gx-md-0">
          <div className="col-md-12 px-md-0 px-3 mt-md-4 mt-3">
            <CategoryChips />
          </div>
        </div>
      </div>
      {/* Empty state — all three result lists finished loading and came
          back empty. Without this the page collapses from Search
          straight into the Footer's "Stay close" block with no margin
          and no signal to the user that the search returned nothing. */}
      {isReady1 &&
        isReady2 &&
        isReady3 &&
        list1.length === 0 &&
        list2.length === 0 &&
        list3.length === 0 && (
          <div className="searchEmpty">
            <div className="searchEmpty__icon">
              <i className="bi bi-search" aria-hidden="true" />
            </div>
            <h3 className="searchEmpty__title">No artworks found</h3>
            <p className="searchEmpty__subtitle">
              {keyword ? (
                <>
                  We couldn't find any artworks matching{" "}
                  <strong>&ldquo;{keyword}&rdquo;</strong>. Try adjusting your
                  search terms or filters.
                </>
              ) : (
                <>
                  No artworks match the current filters. Try broadening your
                  search.
                </>
              )}
            </p>
          </div>
        )}

      {(!isReady2 || list2?.length > 0) && (
        <div className="mt-5 position-relative themedSection themedSection--cream">
          <div className="container py-4 px-md-0 px-3">
            <div className="py-4">
              <CustomTitle
                title="Popular bids"
                link="/explore/popular"
                showAll={true}
              />
            </div>
            {!isReady2 ? (
              <ArtworkCardSkeletonRow />
            ) : (
              <SwipperMultiple
                uniqueId="popularBids"
                slidesData={list2}
                showFireEmoji={true}
              />
            )}
          </div>
        </div>
      )}
      {!token && !user && (
        <div className="col-md-10 mx-auto mt-5">
          <LoginOrRegisterBanner />
        </div>
      )}

      {(!isReady1 || list1?.length > 0) && (
        <div className="mt-5 position-relative">
          <div className="container py-4 px-md-0 px-3">
            <div className="py-4">
              <CustomTitle
                title="Open for bid"
                link="/explore/open-for-bids"
                showAll={true}
              />
            </div>
            {!isReady1 ? (
              <ArtworkCardSkeletonRow />
            ) : (
              <SwipperMultiple uniqueId="openForBids" slidesData={list1} />
            )}
          </div>
        </div>
      )}

      {(!isReady3 || list3?.length > 0) && (
        <div className="mt-5 position-relative themedSection themedSection--cream">
          <div className="container py-4 px-md-0 px-3">
            <div className="py-4">
              <CustomTitle
                title="Upcoming bids"
                link="/explore/upcoming"
                showAll={true}
              />
            </div>
            {!isReady3 ? (
              <ArtworkCardSkeletonRow />
            ) : (
              <SwipperMultiple uniqueId="upcomingBids" slidesData={list3} />
            )}
          </div>
        </div>
      )}
      {/* <div className="container pt-4">
        <div className="row mx-0">
          <div className="col-md-11 mx-auto mb-4">
            <MailingList />
          </div>
        </div>
      </div> */}
      <Footer />
    </>
  );
}

export default SearchResults;
